import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArrowLeft, PencilSimple, Trash, Flask } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PatientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [p, setP] = useState(null);
    const [formulas, setFormulas] = useState([]);
    const [schedules, setSchedules] = useState([]);

    useEffect(() => {
        api.get(`/patients/${id}`).then((r) => setP(r.data));
        api.get(`/patients/${id}/formulas`).then((r) => setFormulas(r.data));
        api.get(`/schedules`, { params: { patient_id: id } }).then((r) => setSchedules(r.data));
    }, [id]);

    const del = async () => {
        if (!window.confirm("¿Eliminar paciente y sus referencias?")) return;
        await api.delete(`/patients/${id}`);
        toast.success("Paciente eliminado");
        navigate("/pacientes");
    };

    if (!p) return <div className="p-12"><span className="label-eyebrow">cargando…</span></div>;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={p.code ? `Paciente · ${p.code}` : "Paciente"}
                title={p.name}
                lead={[p.sex, p.dob, p.activity_level].filter(Boolean).join(" · ") || null}
                actions={
                    <div className="flex items-center gap-2">
                        <Link to={`/pacientes/${id}/editar`} className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2" data-testid="patient-edit-button"><PencilSimple size={14}/> Editar</Link>
                        <Link to={`/formulas/nuevo?patient=${id}`} className="btn-primary inline-flex items-center gap-2" data-testid="patient-formulate-button"><Flask size={14} weight="bold"/> Crear fórmula</Link>
                    </div>
                }
            >
                <div className="mt-4">
                    <Link to="/pacientes" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]">
                        <ArrowLeft size={14}/> Volver a pacientes
                    </Link>
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-8 max-w-5xl grid lg:grid-cols-[1.4fr_1fr] gap-8">
                <div className="space-y-6">
                    <Card title="Identificación">
                        <Row k="Código" v={p.code}/>
                        <Row k="Fecha de nacimiento" v={p.dob}/>
                        <Row k="Sexo" v={p.sex}/>
                    </Card>
                    <Card title="Antropometría">
                        <Row k="Peso" v={p.weight_kg ? `${p.weight_kg} kg` : null}/>
                        <Row k="Altura" v={p.height_cm ? `${p.height_cm} cm` : null}/>
                        <Row k="Actividad" v={p.activity_level}/>
                        <Row k="Objetivo de peso" v={p.weight_goal}/>
                    </Card>
                    <Card title="Objetivos y antecedentes">
                        <ChipsRow k="Objetivos" v={p.objectives}/>
                        <ChipsRow k="Tratamientos estéticos previos" v={p.aesthetic_treatments_history}/>
                        <ChipsRow k="Suplementos actuales" v={p.current_supplements}/>
                        <ChipsRow k="Alergias" v={p.allergies}/>
                    </Card>
                    {(p.medical_notes || p.epigenetics_notes) && (
                        <Card title="Notas">
                            {p.medical_notes && <div><div className="label-eyebrow mb-1.5">Médicas</div><p className="text-sm leading-relaxed">{p.medical_notes}</p></div>}
                            {p.epigenetics_notes && <div className="mt-4"><div className="label-eyebrow mb-1.5">Epigenética</div><p className="text-sm leading-relaxed">{p.epigenetics_notes}</p></div>}
                        </Card>
                    )}
                    <button onClick={del} className="text-sm text-[var(--ink-3)] hover:text-[var(--terra-2)] inline-flex items-center gap-1 mt-4" data-testid="patient-delete-button"><Trash size={14}/> Eliminar paciente</button>
                </div>

                <aside>
                    <Card title={`Fórmulas asociadas (${formulas.length})`}>
                        {formulas.length === 0 ? (
                            <p className="text-sm text-[var(--ink-3)]">Sin fórmulas todavía.</p>
                        ) : (
                            <div className="space-y-2 -m-2">
                                {formulas.map((f) => (
                                    <Link key={f.id} to={`/formulas/${f.id}`} className="block p-3 border hairline hover:bg-[var(--cream)]" data-testid={`patient-formula-${f.id}`}>
                                        <div className="font-medium text-sm leading-tight">{f.title}</div>
                                        <div className="text-xs text-[var(--ink-2)] mt-1">{f.objective} · {(f.components||[]).length} activos</div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </Card>
                    <div className="mt-4">
                        <Card title={`Cronogramas (${schedules.length})`}>
                            {schedules.length === 0 ? (
                                <div className="text-sm text-[var(--ink-3)]">
                                    Sin cronogramas. <Link to={`/cronogramas/nuevo?patient=${id}`} className="text-[var(--sage)] underline">Crear uno →</Link>
                                </div>
                            ) : (
                                <div className="space-y-2 -m-2">
                                    {schedules.map((s) => (
                                        <Link key={s.id} to={`/cronogramas/${s.id}`} className="block p-3 border hairline hover:bg-[var(--cream)]" data-testid={`patient-schedule-${s.id}`}>
                                            <div className="font-medium text-sm leading-tight">{s.name}</div>
                                            <div className="text-xs text-[var(--ink-2)] mt-1">{s.items?.length || 0} fórmulas · {s.safety?.status}</div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Card({ title, children }) {
    return (<section className="border hairline bg-white p-6"><div className="label-eyebrow mb-4">{title}</div><div className="space-y-3">{children}</div></section>);
}
function Row({ k, v }) {
    if (!v) return null;
    return (<div className="flex justify-between gap-4 text-sm border-b hairline last:border-b-0 pb-2 last:pb-0"><span className="text-[var(--ink-2)]">{k}</span><span className="font-medium text-right">{v}</span></div>);
}
function ChipsRow({ k, v }) {
    if (!v || !v.length) return null;
    return (<div><div className="text-xs text-[var(--ink-2)] mb-1.5">{k}</div><div className="flex flex-wrap gap-1.5">{v.map((x, i) => <span key={i} className="citation-badge">{x}</span>)}</div></div>);
}
